import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Phone, MessageSquare, Send, User } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { useAuth } from '../contexts/AuthContext';

const ContactAgentPopup = ({ 
  isOpen, 
  onClose, 
  userEmail: propUserEmail,
  userName: propUserName 
}) => {
  // Obtener email del contexto de autenticación como respaldo
  const { user } = useAuth();
  const userEmail = propUserEmail || user?.email || null;
  const userName = propUserName || user?.displayName || null;
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [phoneError, setPhoneError] = useState('');
  const [messageError, setMessageError] = useState('');

  // Función para validar el teléfono
  const validatePhone = (phoneNumber) => {
    // Remover espacios y guiones
    const cleanPhone = phoneNumber.replace(/[\s-]/g, '');
    
    // Verificar que solo contenga números
    if (!/^\d+$/.test(cleanPhone)) {
      return 'El teléfono solo puede contener números';
    }
    
    // Verificar que tenga exactamente 9 dígitos
    if (cleanPhone.length !== 9) {
      return 'El teléfono debe tener exactamente 9 dígitos';
    }
    
    return '';
  };

  // Función para formatear el teléfono automáticamente
  const formatPhone = (value) => {
    // Remover todo excepto números
    const numbers = value.replace(/\D/g, '');
    
    // Limitar a 9 dígitos
    const limitedNumbers = numbers.slice(0, 9);
    
    // Formatear en grupos de 3
    if (limitedNumbers.length <= 3) {
      return limitedNumbers;
    } else if (limitedNumbers.length <= 6) {
      return `${limitedNumbers.slice(0, 3)} ${limitedNumbers.slice(3)}`;
    } else {
      return `${limitedNumbers.slice(0, 3)} ${limitedNumbers.slice(3, 6)} ${limitedNumbers.slice(6)}`;
    }
  };

  // Función para manejar el cambio del teléfono
  const handlePhoneChange = (e) => {
    const formattedPhone = formatPhone(e.target.value);
    setPhone(formattedPhone);
    
    // Validar en tiempo real
    const error = validatePhone(formattedPhone);
    setPhoneError(error);
  };

  // Función para manejar el cambio del mensaje
  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    if (e.target.value.trim()) {
      setMessageError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar teléfono
    const phoneValidationError = validatePhone(phone);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      return;
    }
    
    // Validar mensaje
    if (!message.trim()) {
      setMessageError('El mensaje es obligatorio');
      return;
    }
    
    // Limpiar errores
    setPhoneError('');
    setMessageError('');

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(process.env.REACT_APP_N8N_WEBHOOK_CONTACT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: phone.trim(),
          message: message.trim(),
          userEmail: userEmail || user?.email || null, // Asegurar que siempre se envíe el email
          userName: userName || user?.displayName || null,
          timestamp: new Date().toISOString(),
          source: 'dashboard_contact_form'
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setPhone('');
        setMessage('');
        // Cerrar popup después de 2 segundos
        setTimeout(() => {
          onClose();
          setSubmitStatus(null);
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setPhone('');
      setMessage('');
      setSubmitStatus(null);
      setPhoneError('');
      setMessageError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  const popupContent = (
    <div className="fixed inset-0 z-[100] bg-black/50 grid place-items-center p-4">
      <div className="bg-card rounded-2xl shadow-xl max-w-md w-full border border-border max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <MessageSquare className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Contactar con un agente
                </h3>
                <p className="text-sm text-muted-foreground">
                  Un agente se pondrá en contacto contigo
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="p-2 hover:bg-secondary rounded-lg transition-colors disabled:opacity-50 text-foreground"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          {submitStatus === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-foreground mb-2">
                ¡Mensaje enviado!
              </h4>
              <p className="text-muted-foreground">
                Un agente se pondrá en contacto contigo pronto.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Phone Input */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Número de teléfono
                </label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="123 456 789"
                  required
                  disabled={isSubmitting}
                  className={`w-full ${phoneError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                />
                {phoneError && (
                  <p className="text-red-500 text-xs mt-1">{phoneError}</p>
                )}
              </div>

              {/* Message Input */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-2" />
                  Mensaje
                </label>
                <textarea
                  value={message}
                  onChange={handleMessageChange}
                  placeholder="Describe tu consulta o pregunta..."
                  required
                  disabled={isSubmitting}
                  rows={4}
                  className={`w-full px-3 py-2 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed text-foreground ${messageError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-border'}`}
                />
                {messageError && (
                  <p className="text-red-500 text-xs mt-1">{messageError}</p>
                )}
              </div>

              {/* User Info */}
              <div className="bg-secondary/50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="w-4 h-4" />
                  <span>Enviando como: {userName || userEmail}</span>
                </div>
              </div>

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3">
                  <p className="text-red-500 text-sm">
                    Error al enviar el mensaje. Por favor, inténtalo de nuevo.
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={handleClose}
                  variant="outline"
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="hero"
                  disabled={isSubmitting || !phone.trim() || !message.trim() || phoneError || messageError}
                  className="flex-1"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar mensaje
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
};

export default ContactAgentPopup;
